import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Trans, withI18n } from 'lingui-react';

import {
  Container,
  Row,
  Column,
} from 'styled-components-reactive-grid';
import { px2rem } from 'decisiv-ui-utils';
import { debounce } from 'lodash';
import { H3 } from 'base-components';
import {
  compose,
  setDisplayName,
} from 'recompose';

import Select from 'elements/Select';

import WizardInput from 'components/WizardInput';
import { parseQueryParams } from 'utils/url';

import Option from './Option';
import DropdownWrapper from './DropdownWrapper';
import withConnectedData from './Container';
import messages from './messages';

/**
 * Component has props and state which need clarification to avoid a confusion
 *
 * 1. this.props.currentAsset: It is the current asset stored in redux.
 *  When you were on asset detail page before you click `Request Service` it has asset detail
 *  in currentAsset under asset reducer
 *
 * 2. this.props.initialAsset: It is the asset selected already. Suppose you selected an asset
 *  and clicked next. Now you clicked prev button to be back to AssetSelectionStep page.
 *  initialAsset is given and it allows this step show asset selected which you selected before.
 *
 * 3. this.state.selectedAsset: This is simply an asset selected on this step.
 *  The difference comparing to this.props.initialAsset, it changes whenever you select an asset
 *  on this step. initialAsset is changed when you go to next step.
 *  Also state selectedAsset keeps the initialAsseet as a default one to actually show
 *  it is selected.
 */

export function makeOptions(assets) {
  return assets.map(asset => ({
    label: `${asset.unitNumber} ${asset.make} - ${asset.model} - ${asset.engine} - ${asset.year}`,
    value: asset,
  }));
}

export class AssetSelectionStep extends Component {
  static propTypes = {
    assets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ).isRequired,
    currentAsset: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
    initialAsset: PropTypes.shape({
      unitNumber: PropTypes.string,
      make: PropTypes.string,
      model: PropTypes.string,
      engine: PropTypes.string,
      year: PropTypes.string,
    }),
    i18n: PropTypes.shape({
      _: PropTypes.func.isRequired,
    }).isRequired,
    clearAssets: PropTypes.func.isRequired,
    loadAssets: PropTypes.func.isRequired,
    loadAsset: PropTypes.func.isRequired,
    setCurrentAsset: PropTypes.func.isRequired,
    submitInputData: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    initialAsset: null,
  };

  state = {
    selectedAsset: this.props.initialAsset && makeOptions([this.props.initialAsset]),
    options: makeOptions(this.props.assets),
  };

  componentDidMount() {
    const { initialAsset, loadAsset } = this.props;
    const assetId = this.getAssetId();
    // loadItems was firing when a asset was already set and causing the wizard to move forward
    // to next step automatically when moving back to asset selection step.
    if (!this.props.location.search) {
      this.loadItems();
    }
    if (initialAsset) {
      return;
    }
    if (assetId) {
      loadAsset(assetId);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.assets !== nextProps.assets) {
      const options = makeOptions(nextProps.assets);

      this.setState({ options });
    }
    if (this.props.currentAsset !== nextProps.currentAsset) {
      const assetId = this.getAssetId();
      const { currentAsset, submitInputData } = nextProps;
      if (currentAsset.id === assetId && assetId) {
        // Clears the url of search query
        this.props.history.push('/service-requests/new');
        submitInputData(currentAsset);
      }
    }
  }

  getAssetId = () => {
    const queries = parseQueryParams(this.props.location.search);
    const { assetId } = queries;
    return assetId;
  }

  loadItems = (value) => {
    if (this.shouldNotReload) {
      this.shouldNotReload = false; // just to be safe
      return;
    }

    this.props.clearAssets();
    const params = { per_page: 150 };
    if (value && value.length >= 3) {
      params.unit_no = value;
    }
    this.props.loadAssets(params);
  }

  handleInputChange = debounce(this.loadItems, 1000);

  handleAssetChange = (options) => {
    const { submitInputData } = this.props;
    const option = options[options.length - 1];
    this.setState({ selectedAsset: options });
    // If asset is changed we clear the query param and replace it with new query
    this.props.setCurrentAsset();
    if (option) {
      // Once an asset is selected, it moves to the next step. At this point,
      // `handleInputChange` is triggered because the input value is removed.
      // In this scenario, since we're moving to the next step, `handleInputChange`
      // should not trigger reloading the assets, as it's a redundant API call.
      this.shouldNotReload = true;

      submitInputData(option.value);
    }
  }

  render() {
    const {
      selectedAsset,
      options,
    } = this.state;
    const {
      i18n,
    } = this.props;

    return (
      <Container style={{ padding: `${px2rem(30)} 0` }}>
        <Row>
          <Column modifiers={['col_offset_3', 'col_6']}>
            <Row>
              <Column modifiers={['col']}>
                <H3 modifiers={['fontWeightRegular']}>
                  <Trans id={messages.whichAsset} />
                </H3>
              </Column>
            </Row>
            <Row style={{ marginTop: px2rem(20) }}>
              <Column modifiers={['col']}>
                <WizardInput label={messages.unit}>
                  <DropdownWrapper>
                    <Select
                      autoBlur
                      multi
                      onChange={this.handleAssetChange}
                      onInputChange={this.handleInputChange}
                      optionRenderer={Option}
                      options={options}
                      placeholder={i18n._(messages.selectItem)}
                      value={selectedAsset}
                    />
                  </DropdownWrapper>
                </WizardInput>
              </Column>
            </Row>
          </Column>
        </Row>
      </Container>
    );
  }
}

export default compose(
  setDisplayName('AssetSelectionStep'),
  withConnectedData,
  withI18n(),
  withRouter,
)(AssetSelectionStep);



// WEBPACK FOOTER //
// ./compositions/AssetSelectionStep/index.js